/**
 * DustSseEngine - the environment-agnostic SSE connection core used by the
 * main-thread DustProvider (direct mode).
 *
 * It owns one logical SSE connection to DUST and all of the reliability behavior:
 *  - eventsource-client (fetch-based) for HTTP status visibility (401/403/400/503)
 *  - heartbeat watchdog (the connected-but-silent guard)
 *  - gapless TTL reconnection (drains the old connection before closing it)
 *  - DDR (Admin Disconnect & Retry) handling
 *  - 503 decorrelated-jitter backoff
 *  - exponential retry/backoff with a /ping + /sse health probe on exhaustion
 *
 * It deliberately knows nothing about the DOM, `navigator`, `window`/`self`, or
 * the MessageSequencer. Hosts react via the callbacks in DustSseEngineCallbacks
 * and drive it with `setConfigAndConnect`, `close`, `ensureConnection`,
 * `forceReconnect`, and `reset`. Environment-specific concerns (online/offline,
 * page freeze/resume) stay in the host.
 */

import { createEventSource } from "eventsource-client";
import type { EventSourceClient } from "eventsource-client";

import { DustLogBuffer } from "./DustLogBuffer";
import {
  configRefreshLeadSeconds,
  defaultHeartbeatTimeoutMs,
  gaplessDrainMs,
  heartbeatCheckIntervalMs,
  maxRetries,
} from "./constants";
import type {
  DustConnectionProbe,
  DustEngineState,
  DustFetch,
  DustMessage,
  DustSseEngineConfig,
  DustSseEngineLiveDiagnostics,
  DustSseEngineOptions,
} from "./types";

export class DustSseEngine {
  private readonly isDevOrStaging: () => boolean;
  private readonly logPrefix: string;
  // Host-supplied fetch. Kept as an instance field so the engine never imports a
  // DOM-dependent fetch directly. See DustFetch / DustSseEngineOptions.fetch.
  private readonly fetch: DustFetch;
  private readonly callbacks: DustSseEngineOptions["callbacks"];
  // Shared (direct mode) or self-owned (worker mode) ring buffer capturing
  // connection-relevant lifecycle logs for diagnostics. See recordLog.
  private readonly logBuffer: DustLogBuffer;

  private config: DustSseEngineConfig | null = null;

  private sseClient: EventSourceClient | null = null;
  private pendingSseClient: EventSourceClient | null = null;
  // During gapless reconnection we keep the previous client open for a fixed
  // window so any messages still in DUST's send buffer can drain before we close
  // it. We always wait the full window — receiving messages on the new
  // connection doesn't mean DUST is done sending on the old one (independent
  // network paths).
  private drainingSseClient: EventSourceClient | null = null;
  private drainTimeoutId: ReturnType<typeof setTimeout> | null = null;

  // Retry count. Incremented ONLY by server-originated failure signals that
  // carry a response: an HTTP 503 and a DDR (admin disconnect & retry). When it
  // reaches maxRetries the engine surfaces a terminal error. A fetch-level
  // failure (no response at all = likely lost connectivity) does NOT change it:
  // we simply retry with the same backoff, so a flaky/absent network never
  // exhausts maxRetries on the user's behalf.
  private retryCount = 0;
  // Set when a DDR (admin disconnect & retry) tears down the connection and
  // schedules a config-refresh-driven reconnect. The reconnect arrives as a
  // fresh setConfigAndConnect, which would otherwise reset retryCount to 0 and
  // lose the DDR's bump — so this flag tells that path to preserve the count.
  private pendingDdrReconnect = false;
  // Consecutive fetch-level failures on the live connection — the request never
  // produced an HTTP response (DNS, TLS, CORS, connection refused). Deliberately
  // separate from retryCount, which only server-originated signals move: a
  // no-response failure must never spend the retry budget, but it is the one
  // symptom that reliably means the path to DUST is dead rather than busy. Any
  // successful connect resets it. Surfaced to hosts via onFetchFailureStreak.
  private consecutiveFetchErrorCount = 0;
  // Set by suspend(). While true the engine will not open a connection for any
  // reason — not a retry, not a lifecycle revival, not a fresh config. Only an
  // explicit resume() lifts it, so a host that has concluded the path to DUST is
  // dead cannot have the connection reopened underneath it by a stray
  // ensureConnection or an in-flight retry.
  private suspended = false;
  private retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ddrTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private ttlTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private connectionInProgress = false;
  private lastReadyState: string | null = null;

  // HTTP status captured by the fetch wrapper
  private lastHttpStatus: number | null = null;
  private lastHttpStatusText: string | null = null;

  // Reason the last fetch() rejected before producing a response (server
  // unreachable, DNS failure, CORS block, abort). The browser hides specifics
  // behind an opaque "Failed to fetch", so we capture the error name/message.
  private lastFetchError: string | null = null;

  // Gapless reconnection state (rcs = reconnection string from TTL message)
  private currentRcs: string | null = null;

  // Last SSE event id from DUST for this mite. Passed as initialLastEventId on
  // every new connection so DUST can replay from the same cursor.
  private lastSseEventId: string | null = null;

  // Timestamp of the last SSE message received (any type)
  private lastMessageReceivedAt: number | null = null;

  // Heartbeat liveness tracking — DUST sends SSE comments (`:`) periodically.
  // We track the last one and declare the connection dead if none arrive within
  // the server-provided timeout.
  private lastHeartbeatAt: number | null = null;
  private heartbeatCheckIntervalId: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimeoutMs = defaultHeartbeatTimeoutMs;
  private lastHeartbeatTimeoutAt: number | null = null;

  // DDR tracking for diagnostics
  private lastDdrAt: number | null = null;
  private lastDdrReason: string | null = null;
  private lastDdrWaitMs: number | null = null;

  // SSE connection response latency
  private lastConnectionLatencyMs: number | null = null;

  // When the currently active SSE connection actually opened (epoch ms). Cleared
  // on close. Used to disambiguate "connected but silent" reports.
  private connectionStartedAt: number | null = null;

  // When the server acknowledged the current connection with a CIR (Connection
  // Is Ready) message. null until ack arrives; cleared on close.
  private cirReceivedAt: number | null = null;

  // Reconnection counters spanning heartbeat-driven and TTL-driven reconnects on
  // the same mite/host. Reset on mite/host change or `reset()`. The first
  // successful connect does not count as a reconnect; subsequent ones do.
  // `gaplessReconnectCount` counts only TTL/force-driven gapless reconnects.
  private reconnectionCount = 0;
  private gaplessReconnectCount = 0;
  private hasConnectedAtLeastOnce = false;

  // Decorrelated jitter backoff state (AWS-style). Every reconnect delay —
  // whether from a soft teardown, a 503, or a DDR — is drawn as
  // random(minBackoffMs, previousBackoffMs * 3) capped at maxBackoffMs, so the
  // feedback term escalates the delay across consecutive failures. Reset to 0
  // on a successful connect / revival so a fresh failure starts from the floor.
  private previousBackoffMs = 0;

  // Set of SSE clients in "draining" mode (gapless reconnection). Their
  // onMessage handler checks this set to route messages correctly.
  private readonly drainingClients = new Set<EventSourceClient>();

  // Monotonic id assigned to each SSE client so lifecycle logs can identify
  // which physical connection they refer to. Two connections briefly coexist
  // during gapless reconnection (live + draining), so their interleaved
  // disconnect logs would otherwise be ambiguous.
  private connectionSeq = 0;
  private readonly clientIds = new WeakMap<EventSourceClient, number>();

  constructor(options: DustSseEngineOptions) {
    this.isDevOrStaging = options.isDevOrStaging;
    this.logPrefix = options.logPrefix;
    this.fetch = options.fetch;
    this.callbacks = options.callbacks;
    this.logBuffer = options.logBuffer ?? new DustLogBuffer();
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Logging
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Short identifying tag for a client in lifecycle logs (e.g. `conn#3`), so
   * live-vs-draining connection logs are unambiguous when they interleave.
   *
   * @param client The SSE client to label.
   * @return The tag, or `conn#?` if the client has no assigned id.
   */
  private clientLabel(client: EventSourceClient): string {
    return `conn#${this.clientIds.get(client) ?? "?"}`;
  }

  /**
   * Record a connection-relevant log line into the diagnostics buffer (always,
   * regardless of environment — the buffer is the prod diagnostics source). The
   * buffer serializes and appends any extra args.
   *
   * @param message Log message (without prefix).
   * @param args Extra args to serialize alongside the message.
   */
  private recordLog(message: string, args: unknown[]): void {
    this.logBuffer.record(`${this.logPrefix} ${message}`, args);
  }

  private log(message: string, ...args: unknown[]): void {
    this.recordLog(message, args);
    if (this.isDevOrStaging()) {
      console.log(`${this.logPrefix} ${message}`, ...args);
    }
  }

  private logWarn(message: string, ...args: unknown[]): void {
    this.recordLog(message, args);
    if (this.isDevOrStaging()) {
      console.warn(`${this.logPrefix} ${message}`, ...args);
    }
  }

  private logError(message: string, ...args: unknown[]): void {
    this.recordLog(message, args);
    if (this.isDevOrStaging()) {
      console.error(`${this.logPrefix} ${message}`, ...args);
    }
  }

  /**
   * Console-only log for high-volume per-message events (message receipt,
   * heartbeat comments). Deliberately NOT recorded into the diagnostics buffer
   * to keep it from being dominated by spam.
   *
   * @param message Log message (without prefix).
   * @param args Extra args.
   */
  private logVerbose(message: string, ...args: unknown[]): void {
    if (this.isDevOrStaging()) {
      console.log(`${this.logPrefix} ${message}`, ...args);
    }
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Config / lifecycle
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Returns true when the given config's auth token is expired or close enough
   * to expiry that we should refresh rather than reuse it. A config without an
   * expiration is treated as non-expiring.
   */
  isConfigExpired(): boolean {
    if (!this.config?.expiration) {
      return false;
    }
    return Date.now() / 1000 > this.config.expiration - configRefreshLeadSeconds;
  }

  /**
   * Whether the engine currently has connection config.
   */
  hasConfig(): boolean {
    return this.config !== null;
  }

  /**
   * Set/replace config and connect. Mirrors the worker's
   * startConnectionWithConfig: on mite/host change it tears down the existing
   * connection and resets per-session counters (emitting onIdentityChange).
   */
  setConfigAndConnect(config: DustSseEngineConfig): void {
    this.log("setConfigAndConnect called", {
      hasExistingConfig: !!this.config,
      hasSseClient: !!this.sseClient,
    });

    if (this.config && (this.config.mite !== config.mite || this.config.dustHost !== config.dustHost)) {
      this.log("Config identity changed, closing existing connection");
      this.close();
      this.currentRcs = null;
      this.lastSseEventId = null;
      this.retryCount = 0;
      this.resetBackoff();
      this.consecutiveFetchErrorCount = 0;
      this.pendingDdrReconnect = false;
      this.reconnectionCount = 0;
      this.gaplessReconnectCount = 0;
      this.hasConnectedAtLeastOnce = false;
      this.callbacks.onIdentityChange?.();
    }

    this.config = config;

    if (this.isConfigExpired()) {
      this.log("Config expired, requesting refresh");
      this.callbacks.onConfigRefreshNeeded();
      return;
    }

    if (!this.sseClient && !this.connectionInProgress) {
      // A fresh connection start (e.g. startSubscription after a prior
      // endSubscription) gets a full retry budget. close() preserves retryCount
      // so a single retry cycle survives transient teardown, but a brand-new
      // start from idle must not inherit an exhausted count — otherwise the next
      // failure would emit a terminal error with zero retry attempts. The retry
      // backoff path calls startConnection() directly, so this branch is only
      // reached for genuine fresh starts, never mid-retry-cycle. A DDR-driven
      // reconnect arrives here too, but must preserve the DDR's retryCount bump
      // so the new connection's `attempts` query param reflects it.
      if (!this.pendingDdrReconnect) {
        this.retryCount = 0;
        this.resetBackoff();
      }
      this.pendingDdrReconnect = false;
      this.log("Starting connection (no existing connection)");
      this.startConnection();
    } else {
      this.log("Skipping connection (already connected or connecting)");
    }
  }

  /**
   * Returns true when the active SSE connection appears alive — i.e. we have a
   * client and the heartbeat watchdog would not currently declare it dead. Same
   * staleness threshold the watchdog uses, so the watchdog and external callers
   * (ensureConnection) agree on "healthy". eventsource-client's "connected"
   * state alone is insufficient: a TCP socket can stay open silently while DUST
   * stops sending events; the heartbeat is the only signal that catches that.
   */
  isHealthy(): boolean {
    if (!this.sseClient) {
      return false;
    }
    if (this.lastHeartbeatAt === null) {
      return false;
    }
    return Date.now() - this.lastHeartbeatAt < this.heartbeatTimeoutMs;
  }

  /**
   * Snapshot of the engine's connection/retry state for host diagnostics. Hosts
   * may spread additional host-owned fields (broadcast errors, port timings).
   */
  getState(): DustEngineState {
    return {
      retryCount: this.retryCount,
      maxRetries,
      consecutiveFetchErrorCount: this.consecutiveFetchErrorCount,
      suspended: this.suspended,
      isRetrying: this.retryTimeoutId !== null,
      heartbeatTimeoutMs: this.heartbeatTimeoutMs,
      reconnectionCount: this.reconnectionCount,
      gaplessReconnectCount: this.gaplessReconnectCount,
      ...(this.lastReadyState !== null ? { lastReadyState: this.lastReadyState } : {}),
      ...(this.lastMessageReceivedAt !== null ? { lastMessageReceivedAt: this.lastMessageReceivedAt } : {}),
      ...(this.lastHttpStatus !== null ? { lastHttpStatus: this.lastHttpStatus } : {}),
      ...(this.lastHttpStatusText !== null ? { lastHttpStatusText: this.lastHttpStatusText } : {}),
      ...(this.lastFetchError !== null ? { lastFetchError: this.lastFetchError } : {}),
      ...(this.lastHeartbeatAt !== null ? { lastHeartbeatAt: this.lastHeartbeatAt } : {}),
      ...(this.lastHeartbeatTimeoutAt !== null ? { lastHeartbeatTimeoutAt: this.lastHeartbeatTimeoutAt } : {}),
      ...(this.lastDdrAt !== null
        ? {
            lastDdrAt: this.lastDdrAt,
            lastDdrReason: this.lastDdrReason,
            lastDdrWaitMs: this.lastDdrWaitMs ?? undefined,
          }
        : {}),
      ...(this.lastConnectionLatencyMs !== null ? { lastConnectionLatencyMs: this.lastConnectionLatencyMs } : {}),
      ...(this.connectionStartedAt !== null ? { connectionStartedAt: this.connectionStartedAt } : {}),
      ...(this.cirReceivedAt !== null ? { cirReceivedAt: this.cirReceivedAt } : {}),
      ...(this.lastSseEventId !== null ? { lastSseEventId: this.lastSseEventId } : {}),
    };
  }

  /**
   * Live, non-snapshot accessors for hosts that surface diagnostics on demand
   * (the worker's get_diagnostics) precisely when the lifecycle snapshot may be
   * stale.
   */
  getLiveDiagnostics(): DustSseEngineLiveDiagnostics {
    return {
      lastMessageReceivedAt: this.lastMessageReceivedAt,
      lastHeartbeatAt: this.lastHeartbeatAt,
      connectionStartedAt: this.connectionStartedAt,
      cirReceivedAt: this.cirReceivedAt,
      reconnectionCount: this.reconnectionCount,
      gaplessReconnectCount: this.gaplessReconnectCount,
      lastDdrAt: this.lastDdrAt,
      lastDdrReason: this.lastDdrReason,
      lastDdrWaitMs: this.lastDdrWaitMs,
    };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Gapless reconnection drain management
  // ──────────────────────────────────────────────────────────────────────────

  private closeDrainingConnection(reason: string): void {
    if (this.drainTimeoutId !== null) {
      clearTimeout(this.drainTimeoutId);
      this.drainTimeoutId = null;
    }
    if (this.drainingSseClient) {
      this.log(`Gapless reconnection: closing drained ${this.clientLabel(this.drainingSseClient)} (${reason})`);
      this.drainingClients.delete(this.drainingSseClient);
      this.drainingSseClient.close();
      this.drainingSseClient = null;
    }
  }

  /**
   * Tear down a specific draining client when its own connection ends (clean EOF
   * via onDisconnect, or an abrupt close/TCP-reset surfaced as a fetch-level
   * failure via onScheduleReconnect). Returns true if `client` was a draining
   * client and was handled — callers must then return without applying the
   * active-connection retry policy, since a draining connection closing is
   * expected and must not trigger a reconnect of the live connection.
   */
  private cleanupDrainingClient(client: EventSourceClient): boolean {
    if (!this.drainingClients.has(client)) {
      return false;
    }
    this.log(`Draining connection disconnected, cleaning up (${this.clientLabel(client)})`);
    this.drainingClients.delete(client);
    if (this.drainingSseClient === client) {
      if (this.drainTimeoutId !== null) {
        clearTimeout(this.drainTimeoutId);
        this.drainTimeoutId = null;
      }
      this.drainingSseClient = null;
    }
    client.close();
    return true;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Retry / backoff
  // ──────────────────────────────────────────────────────────────────────────

  /**
   * Decorrelated jitter backoff:
   *   sleep = min(maxBackoffMs, random_between(minBackoffMs, previousBackoffMs * 3))
   * The previous delay feeds the next, so consecutive failures escalate the
   * wait (up to the cap) while jitter spreads concurrent clients to avoid a
   * thundering herd. State is reset via `resetBackoff` on a clean slate.
   *
   * @return The backoff delay in ms, also stored as the feedback term.
   */
  private computeBackoffMs(): number {
    const minBackoffMs = 1000;
    const maxBackoffMs = 30000;
    const prev = this.previousBackoffMs || minBackoffMs;
    const sleep = Math.min(maxBackoffMs, minBackoffMs + Math.random() * (prev * 3 - minBackoffMs));
    this.previousBackoffMs = Math.round(sleep);
    return this.previousBackoffMs;
  }

  /**
   * Reset the decorrelated-jitter feedback term so the next backoff starts from
   * the floor. Note: does NOT cancel any pending retry timer — `close()` owns
   * timer teardown.
   */
  private resetBackoff(): void {
    this.previousBackoffMs = 0;
  }

  /**
   * Reset the retry count, backoff and fetch-failure streak. Called whenever we
   * get a clean slate: a successful connect, a config-identity change, a full
   * reset, or a connectivity-driven revival (online/resume/bfcache). Clearing
   * the streak here matters most on revival: an offline tab produces
   * fetch-level failures for an entirely recoverable reason, and those must not
   * be held against DUST once connectivity is back.
   */
  private resetRetryState(): void {
    this.retryCount = 0;
    this.resetBackoff();
    this.consecutiveFetchErrorCount = 0;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Heartbeat liveness
  // ──────────────────────────────────────────────────────────────────────────

  private startHeartbeatCheck(): void {
    this.stopHeartbeatCheck();
    this.lastHeartbeatAt = Date.now();
    this.heartbeatCheckIntervalId = setInterval(() => {
      if (this.lastHeartbeatAt !== null && Date.now() - this.lastHeartbeatAt > this.heartbeatTimeoutMs) {
        this.lastHeartbeatTimeoutAt = Date.now();
        this.log("Heartbeat timeout — connection presumed dead, reconnecting");
        this.close();
        this.retryConnectionWithBackoff();
      }
    }, heartbeatCheckIntervalMs);
  }

  /**
   * Stop the heartbeat watchdog. Hosts call this when going offline so the
   * watchdog doesn't fire on a deliberately-closed connection.
   */
  stopHeartbeatCheck(): void {
    if (this.heartbeatCheckIntervalId !== null) {
      clearInterval(this.heartbeatCheckIntervalId);
      this.heartbeatCheckIntervalId = null;
    }
    this.lastHeartbeatAt = null;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DDR / TTL message handlers
  // ──────────────────────────────────────────────────────────────────────────

  private handleDdrMessage(rMs: number, reason?: string): void {
    if (typeof rMs !== "number") {
      return;
    }

    // A DDR is a server-originated signal, so it increments the retry count. The
    // bump also surfaces on the next connection's `attempts` query param. Reset
    // to 0 on the next successful connect. The reconnect comes back through
    // setConfigAndConnect as a fresh start, so flag it to preserve this bump
    // rather than resetting the count.
    this.retryCount++;

    if (this.retryCount >= maxRetries) {
      this.logError(`DDR received but retry budget exhausted (${this.retryCount}/${maxRetries})`);
      this.currentRcs = null;
      this.close();
      this.emitTerminalError();
      return;
    }

    this.pendingDdrReconnect = true;

    const backoffMs = this.computeBackoffMs();
    const jitter = Math.random() * rMs * 0.3;
    const waitMs = Math.round(backoffMs + rMs + jitter);
    const reasonStr = reason ? ` (${reason})` : "";

    this.lastDdrAt = Date.now();
    this.lastDdrReason = reason ?? null;
    this.lastDdrWaitMs = waitMs;

    this.log(
      `Admin requested disconnect${reasonStr}, reconnecting in ${waitMs}ms (backoff=${backoffMs}, r_ms=${rMs})`
    );

    if (this.ddrTimeoutId !== null) {
      clearTimeout(this.ddrTimeoutId);
    }

    this.currentRcs = null;
    this.close();

    // Schedule reconnection — request fresh config since auth may have changed.
    this.ddrTimeoutId = setTimeout(() => {
      this.ddrTimeoutId = null;
      this.callbacks.onConfigRefreshNeeded();
    }, waitMs);
  }

  private handleTtlMessage(tMs: number, rcs: string, siS?: number): void {
    if (typeof tMs !== "number") {
      return;
    }

    this.log(`TTL set to ${tMs}ms, will perform gapless reconnection when expired`);

    if (typeof rcs === "string") {
      this.currentRcs = rcs;
    }

    // Server-suggested idle timeout overrides the default heartbeat timeout. The
    // server already incorporates missed-heartbeat tolerance into si_s.
    if (typeof siS === "number" && siS > 0) {
      this.log(`Server suggested idle timeout: ${siS}s`);
      this.heartbeatTimeoutMs = siS * 1000;
      // Re-arm the watchdog from "now" against the new (possibly larger) budget.
      this.lastHeartbeatAt = Date.now();
    }

    if (this.ttlTimeoutId !== null) {
      clearTimeout(this.ttlTimeoutId);
    }

    this.ttlTimeoutId = setTimeout(() => {
      this.ttlTimeoutId = null;
      this.log("TTL expired, performing gapless reconnection");
      this.startGaplessReconnection();
    }, tMs);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Message handling
  // ──────────────────────────────────────────────────────────────────────────

  private handleMessage(data: string, fromDrainingConnection: boolean): void {
    this.lastMessageReceivedAt = Date.now();

    // Any inbound traffic on the active connection proves DUST isn't silent, so
    // it counts as a liveness signal for the heartbeat watchdog. Draining
    // connections are independent network paths, so their traffic does NOT prove
    // the active connection is alive.
    if (!fromDrainingConnection) {
      this.lastHeartbeatAt = Date.now();
    }

    let message: Record<string, unknown>;
    try {
      message = JSON.parse(data) as Record<string, unknown>;
    } catch (error) {
      this.logError("Failed to parse message", error);
      return;
    }

    if (!message.type) {
      this.logError("Received message without type", message);
      return;
    }

    this.logVerbose(
      `Received message of type '${message.type as string}'${fromDrainingConnection ? " (draining)" : ""}`,
      message
    );

    // From a draining connection, only forward dashboard events. Ignore control
    // messages so DDR/TTL on the old path can't tear down or overwrite
    // reconnection state belonging to the new active connection.
    if (fromDrainingConnection) {
      if (message.type === "evt") {
        this.callbacks.onEvent(message as unknown as DustMessage, true);
      }
      return;
    }

    if (message.type === "ddr" && message.body) {
      const body = message.body as Record<string, unknown>;
      this.handleDdrMessage(body.r_ms as number, body.e as string | undefined);
      return;
    }

    if (message.type === "ttl" && message.body) {
      const body = message.body as Record<string, unknown>;
      this.handleTtlMessage(body.t_ms as number, body.rcs as string, body.si_s as number | undefined);
      return;
    }

    // CIR (Connection Is Ready) — server ack of the active connection.
    if (message.type === "cir") {
      this.cirReceivedAt = Date.now();
      this.log("Connection is ready (cir) received");
      return;
    }

    if (message.type === "evt") {
      this.callbacks.onEvent(message as unknown as DustMessage, false);
      return;
    }

    this.log(`Unhandled message type '${message.type as string}'`);
  }

  // ──────────────────────────────────────────────────────────────────────────
  // DUST health probe — lightweight /ping + /sse check after retries exhausted
  // ──────────────────────────────────────────────────────────────────────────

  private async probeDustHealth(): Promise<DustConnectionProbe> {
    const base: DustConnectionProbe = {
      lastSseStatus: this.lastHttpStatus,
      lastSseStatusText: this.lastHttpStatusText,
      pingStatus: null,
      pingOk: null,
      sseProbeStatus: null,
    };

    if (!this.config) {
      return { ...base, pingError: "no config" };
    }

    const { dustHost } = this.config;
    const dustHostWithScheme = /^https?:\/\//i.test(dustHost) ? dustHost : `https://${dustHost}`;

    const probeEndpoint = async (
      path: string
    ): Promise<{ status: number | null; ok: boolean | null; error?: string }> => {
      const controller = new AbortController();
      const tid = setTimeout(() => controller.abort(), 5000);
      try {
        const response = await this.fetch(`${dustHostWithScheme}${path}`, { signal: controller.signal });
        clearTimeout(tid);
        return { status: response.status, ok: response.ok };
      } catch (err: unknown) {
        clearTimeout(tid);
        const msg = err instanceof Error ? err.message : String(err);
        return { status: null, ok: false, error: msg };
      }
    };

    const [pingResult, sseResult] = await Promise.all([probeEndpoint("/ping"), probeEndpoint("/sse")]);

    return {
      ...base,
      pingStatus: pingResult.status,
      pingOk: pingResult.ok,
      ...(pingResult.error ? { pingError: pingResult.error } : {}),
      sseProbeStatus: sseResult.status,
      ...(sseResult.error ? { sseProbeError: sseResult.error } : {}),
    };
  }

  // ──────────────────────────────────────────────────────────────────────────
  // SSE URL builder
  // ──────────────────────────────────────────────────────────────────────────

  private buildSseUrl(): string | null {
    if (!this.config) {
      return null;
    }

    const { dustHost, mite, auth } = this.config;
    const dustHostWithScheme = /^https?:\/\//i.test(dustHost) ? dustHost : `https://${dustHost}`;

    let queryString = `mite=${encodeURIComponent(mite)}&attempts=${this.retryCount}`;
    if (auth) {
      queryString += `&auth=${encodeURIComponent(auth)}`;
    }
    if (this.currentRcs) {
      queryString += `&rcs=${encodeURIComponent(this.currentRcs)}`;
    }

    return `${dustHostWithScheme}/sse?${queryString}`;
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Connection management
  // ──────────────────────────────────────────────────────────────────────────

  private emitTerminalError(): void {
    this.logError(`Max retry attempts (${maxRetries}) reached`);
    const retryCountAtFailure = this.retryCount;
    this.probeDustHealth().then((probe) => {
      if (this.sseClient || this.retryCount !== retryCountAtFailure) {
        this.log("Skipping stale error broadcast — connection recovered during probe");
        return;
      }
      this.callbacks.onError(`Max retry attempts (${maxRetries}) reached`, probe);
    });
  }

  /**
   * Render a fetch() rejection into a concise, loggable string. The browser
   * deliberately collapses most network failures into an opaque
   * `TypeError: Failed to fetch`, so the most useful signal is the error
   * name plus message; an `AbortError` name additionally tells us the request
   * was cancelled rather than refused.
   *
   * @param error the value thrown by the wrapped fetch()
   * @return a short description such as `TypeError: Failed to fetch`
   */
  private describeFetchError(error: unknown): string {
    if (error instanceof Error) {
      const name = error.name || "Error";
      return error.message ? `${name}: ${error.message}` : name;
    }
    return String(error);
  }

  private startConnection(oldClientToClose?: EventSourceClient): void {
    if (this.suspended) {
      this.log("Suspended, not opening a connection");
      return;
    }

    if (!this.config) {
      return;
    }

    if (this.sseClient && !oldClientToClose) {
      this.logWarn("startConnection called but connection already exists!");
      return;
    }

    if (this.connectionInProgress && !oldClientToClose) {
      this.logWarn("startConnection called but connection attempt already in progress!");
      return;
    }

    this.connectionInProgress = true;
    const subscribeUrl = this.buildSseUrl();

    if (!subscribeUrl) {
      this.connectionInProgress = false;
      return;
    }

    const connectionId = ++this.connectionSeq;
    // Not the URL: it carries the auth JWT and the rcs, and this buffer is sent to Sentry.
    this.log(`Creating SSE connection (conn#${connectionId}):`, {
      dustHost: this.config.dustHost,
      mite: this.config.mite,
      attempts: this.retryCount,
      hasAuth: !!this.config.auth,
      hasRcs: this.currentRcs !== null,
      lastSseEventId: this.lastSseEventId,
    });

    // Reset stale HTTP status so it can't affect onConnect guards.
    this.lastHttpStatus = null;
    this.lastHttpStatusText = null;
    this.lastFetchError = null;

    // Per-connection ack/start markers belong to the active SSE connection.
    this.cirReceivedAt = null;
    this.connectionStartedAt = null;

    try {
      // Tracks whether onDisconnect already handled this connection's teardown.
      // eventsource-client calls onScheduleReconnect both after fetch rejection
      // (where onDisconnect does NOT fire) and after stream EOF (where it does).
      let disconnectHandled = false;

      const newClient = createEventSource({
        url: subscribeUrl,
        initialLastEventId: this.lastSseEventId ?? undefined,

        // Wrap fetch to capture HTTP status codes native EventSource hides, track
        // latency, and handle non-200 statuses per DUST API docs.
        fetch: async (url, init) => {
          const fetchStart = Date.now();
          let response: Awaited<ReturnType<typeof this.fetch>>;
          try {
            response = await this.fetch(url as string, init as RequestInit);
          } catch (error) {
            // fetch() rejects (rather than returning a non-2xx response) when
            // the request never completes: connection refused, DNS failure,
            // TLS error, CORS preflight rejection, or an abort. Capture and log
            // the detail the browser does expose, then re-throw so
            // eventsource-client still drives its reconnect. We log here (not
            // only in onScheduleReconnect) so the diagnostics buffer always
            // records the reason even when a downstream handler short-circuits.
            this.lastConnectionLatencyMs = Date.now() - fetchStart;
            this.lastFetchError = this.describeFetchError(error);
            this.logError(
              `Fetch rejected (${this.lastFetchError}) — server unreachable, DNS, TLS, CORS, or aborted`
            );
            throw error;
          }
          this.lastConnectionLatencyMs = Date.now() - fetchStart;
          this.lastFetchError = null;
          this.lastHttpStatus = response.status;
          this.lastHttpStatusText = response.statusText;

          if (response.status === 401 || response.status === 403) {
            let reason: string | null = null;
            try {
              reason = await response.clone().text();
              if (reason && reason.length > 500) {
                reason = reason.substring(0, 500) + "...";
              }
            } catch (_) {
              // body read failed
            }
            this.callbacks.onAuthError(response.status, reason);
            this.callbacks.onConfigRefreshNeeded();

            return { body: null, url: response.url, status: 204, redirected: false };
          }

          if (response.status === 400) {
            let reason: string | null = null;
            try {
              reason = await response.clone().text();
              if (reason && reason.length > 500) {
                reason = reason.substring(0, 500) + "...";
              }
            } catch (_) {
              // body read failed
            }
            this.logError("Bad request (400) — malformed MITE or config", reason);
            this.callbacks.onError(`HTTP 400: ${reason ?? "bad request"}`);
            this.callbacks.onConfigRefreshNeeded();

            return { body: null, url: response.url, status: 204, redirected: false };
          }

          if (response.status === 503) {
            // A 503 is a real server response (service up but unavailable), so it
            // increments the retry count. Incrementing here — when the response
            // arrives — rather than just before the next request keeps the count
            // tied to actual server signals.
            this.retryCount++;
            this.connectionInProgress = false;

            if (this.retryCount >= maxRetries) {
              this.logError(
                `Service unavailable (503) and retry budget exhausted (${this.retryCount}/${maxRetries})`
              );
              this.emitTerminalError();
              return { body: null, url: response.url, status: 204, redirected: false };
            }

            this.log(`Service unavailable (503), scheduling retry (budget ${this.retryCount}/${maxRetries})`);
            this.retryConnectionWithBackoff();

            return { body: null, url: response.url, status: 204, redirected: false };
          }

          return response;
        },

        onConnect: () => {
          // If this client was aborted by close(), bail out.
          if (this.pendingSseClient !== newClient && this.sseClient !== newClient) {
            newClient.close();
            return;
          }

          // Non-success responses return synthetic 204 — eventsource-client calls
          // onConnect before it checks the 204 and calls onDisconnect. Suppress
          // the false "connected"; the fetch wrapper already handled the error.
          if (
            this.lastHttpStatus === 401 ||
            this.lastHttpStatus === 403 ||
            this.lastHttpStatus === 400 ||
            this.lastHttpStatus === 503
          ) {
            return;
          }

          this.pendingSseClient = null;

          if (oldClientToClose) {
            // If a previous gapless reconnection is still draining, close it
            // immediately — it's been superseded.
            this.closeDrainingConnection("superseded by new gapless reconnection");

            this.log(
              `Gapless reconnection: ${this.clientLabel(newClient)} established, draining old ${this.clientLabel(oldClientToClose)} for up to ${gaplessDrainMs}ms`
            );

            this.drainingSseClient = oldClientToClose;
            this.drainingClients.add(oldClientToClose);
            this.drainTimeoutId = setTimeout(() => {
              this.drainTimeoutId = null;
              this.closeDrainingConnection("drain timeout reached");
            }, gaplessDrainMs);
          } else {
            this.log(`Connection OPENED successfully (${this.clientLabel(newClient)})`);
          }

          this.sseClient = newClient;
          this.connectionStartedAt = Date.now();
          if (this.hasConnectedAtLeastOnce) {
            this.reconnectionCount++;
            if (oldClientToClose) {
              this.gaplessReconnectCount++;
            }
          }
          this.hasConnectedAtLeastOnce = true;
          this.connectionInProgress = false;
          this.retryCount = 0;
          this.consecutiveFetchErrorCount = 0;
          this.pendingDdrReconnect = false;
          this.lastReadyState = null;
          this.resetBackoff();
          this.startHeartbeatCheck();
          this.callbacks.onConnected();
        },

        onMessage: (msg) => {
          if (typeof msg.id === "string") {
            this.lastSseEventId = msg.id;
          }

          // eventsource-client delivers ALL SSE events through onMessage. DUST
          // sends events with `event: msg`, so filter for that.
          if (msg.event !== "msg") {
            return;
          }
          const fromDraining = this.drainingClients.has(newClient);
          this.handleMessage(msg.data, fromDraining);
        },

        onComment: (comment: string) => {
          this.lastHeartbeatAt = Date.now();
          this.logVerbose(`Comment received: ${comment}`);
        },

        onDisconnect: () => {
          disconnectHandled = true;

          // 503 returns synthetic 204 which triggers onDisconnect, but the fetch
          // wrapper already scheduled a decorrelated jitter retry. Just close.
          if (this.lastHttpStatus === 503) {
            newClient.close();
            return;
          }

          // If this client was demoted to draining, clean it up — don't touch
          // the new active connection.
          if (this.cleanupDrainingClient(newClient)) {
            return;
          }

          // cleanupDrainingClient closes the drained client, and the library
          // re-invokes onDisconnect synchronously from that close(). By then the
          // client is no longer the active or pending connection, so this stale
          // re-entrant callback must not tear down the live connection.
          if (this.sseClient !== newClient && this.pendingSseClient !== newClient) {
            newClient.close();
            return;
          }

          // eventsource-client auto-reconnects by default. We manage retries
          // ourselves, so close on disconnect to prevent double reconnection.
          const readyState = newClient.readyState;
          this.lastReadyState = readyState;

          this.logError(`Connection DISCONNECTED (${this.clientLabel(newClient)})`, { readyState });

          // Gapless reconnection that failed before opening: fall back to old.
          if (oldClientToClose && this.sseClient !== newClient) {
            this.log(
              `Gapless reconnection ${this.clientLabel(newClient)} failed, keeping old ${this.clientLabel(oldClientToClose)}`
            );
            newClient.close();
            this.connectionInProgress = false;
            this.retryConnectionWithBackoff();
            return;
          }

          newClient.close();
          this.close();

          // A clean stream EOF is not a server error-code signal, so it never
          // increments the retry count — soft-retry indefinitely.
          this.retryConnectionWithBackoff();
        },

        onScheduleReconnect: () => {
          // Two cases:
          //  (a) fetch() rejected (network/DNS/CORS) — onDisconnect was NOT called
          //  (b) SSE stream ended normally — onDisconnect was already called
          // For (b) this is a no-op. For (a) the library would silently loop with
          // its own 2s reconnect without our retry policy ever running.
          if (disconnectHandled) {
            return;
          }

          // A draining connection closing at the fetch level (the server resets
          // the old socket after its TTL rather than cleanly EOF-ing it) is
          // expected — onDisconnect does not fire in that case, so handle it
          // here. Don't log an error or schedule a retry: the new active
          // connection is healthy.
          if (this.cleanupDrainingClient(newClient)) {
            return;
          }

          // A genuine no-response failure on this tab's own connection. Report
          // the streak before applying the retry policy so a host can give up on
          // DUST while the engine goes on retrying for anyone who can't.
          this.consecutiveFetchErrorCount++;

          this.logError(
            `Fetch-level failure on ${this.clientLabel(newClient)} (${this.lastFetchError ?? "unknown network/DNS/CORS error"}), applying retry policy (streak ${this.consecutiveFetchErrorCount})`
          );

          this.callbacks.onFetchFailureStreak?.(this.consecutiveFetchErrorCount);

          // If we don't have an internet connection, then start the backoff from the beginning.
          this.resetBackoff();

          newClient.close();
          this.connectionInProgress = false;

          // Gapless reconnection that failed at fetch level: fall back to old.
          if (oldClientToClose && this.sseClient !== newClient) {
            this.log(
              `Gapless reconnection ${this.clientLabel(newClient)} fetch failed, keeping old ${this.clientLabel(oldClientToClose)}`
            );
            this.retryConnectionWithBackoff();
            return;
          }

          if (this.sseClient === newClient) {
            this.close();
          }
          this.retryConnectionWithBackoff();
        },
      });

      this.clientIds.set(newClient, connectionId);

      // eventsource-client auto-connects, so newClient is now connecting. Close
      // any previously pending client that never opened (defense-in-depth).
      if (this.pendingSseClient) {
        this.pendingSseClient.close();
      }
      this.pendingSseClient = newClient;
    } catch (error) {
      if (this.pendingSseClient) {
        this.pendingSseClient.close();
        this.pendingSseClient = null;
      }
      this.connectionInProgress = false;
      this.logError("Failed to create SSE connection:", error);
      this.callbacks.onError(String(error));
    }
  }

  /**
   * Start gapless reconnection — opens a new connection before closing the old
   * one so no messages are lost during TTL-based or forced reconnection.
   */
  startGaplessReconnection(): void {
    if (!this.config) {
      return;
    }

    if (this.connectionInProgress) {
      this.log("Skipping gapless reconnection, connection attempt already in progress");
      return;
    }

    if (this.isConfigExpired()) {
      this.log("Auth token expired during gapless reconnection, requesting fresh config");
      this.close();
      this.callbacks.onConfigRefreshNeeded();
      return;
    }

    this.startConnection(this.sseClient ?? undefined);
  }

  /**
   * Public alias for forced gapless reconnection (e.g. a streaming-timeout the
   * caller observed on a connection the engine still considers healthy).
   * Throttling, if any, is the host's responsibility.
   */
  forceReconnect(): void {
    this.startGaplessReconnection();
  }

  /**
   * If we should have a healthy connection but don't — and nothing else is
   * already arranging one — kick off a connection attempt. Returns true if a new
   * attempt was scheduled. Called from host signals (worker pong, tab
   * online/resume) so a silently-dead connection self-heals without waiting for
   * the watchdog tick.
   */
  ensureConnection(reason: string): boolean {
    if (this.suspended) {
      return false;
    }

    if (!this.config) {
      return false;
    }
    if (this.connectionInProgress || this.pendingSseClient) {
      return false;
    }
    if (this.retryTimeoutId !== null || this.ddrTimeoutId !== null) {
      return false;
    }
    if (this.isConfigExpired()) {
      this.log(`ensureConnection (${reason}): config expired, requesting refresh`);
      this.callbacks.onConfigRefreshNeeded();
      return false;
    }
    if (!this.sseClient) {
      this.log(`ensureConnection (${reason}): no active connection, starting one`);
      // Connectivity-driven revival (online/resume/bfcache): reset the retry
      // count so prior outage increments don't kill the first attempt.
      this.resetRetryState();
      this.startConnection();
      return true;
    }
    if (!this.isHealthy()) {
      this.log(`ensureConnection (${reason}): active SSE is unhealthy, force-reconnecting gaplessly`);
      this.resetRetryState();
      this.startGaplessReconnection();
      return true;
    }
    return false;
  }

  /**
   * Schedule a reconnect after a decorrelated-jitter backoff. The retry count
   * is incremented by the caller, not here: server error-code signals (503, DDR)
   * increment it before calling, while soft teardowns — clean stream
   * EOF, fetch-level rejections (no response), heartbeat timeouts — leave it
   * untouched so a lack of connectivity is never held against the user and they
   * retry indefinitely. Either way the backoff delay escalates via the shared
   * feedback term.
   */
  private retryConnectionWithBackoff(): void {
    if (this.suspended) {
      return;
    }

    const backoffMs = this.computeBackoffMs();

    this.log(`Retrying connection in ${backoffMs}ms (current retry/max: ${this.retryCount}/${maxRetries})`);

    this.retryTimeoutId = setTimeout(() => {
      this.retryTimeoutId = null;
      if (this.isConfigExpired()) {
        this.callbacks.onConfigRefreshNeeded();
      } else {
        this.startConnection();
      }
    }, backoffMs);
  }

  /**
   * Close the active SSE connection and cancel all pending timers. Preserves
   * currentRcs and config so the next connection can prove prior lease
   * ownership. Use `reset()` to fully clear session state.
   */
  close(): void {
    this.connectionInProgress = false;

    if (this.pendingSseClient) {
      this.pendingSseClient.close();
      this.pendingSseClient = null;
    }

    this.closeDrainingConnection("connection closed");
    this.stopHeartbeatCheck();

    if (this.retryTimeoutId !== null) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
    if (this.ddrTimeoutId !== null) {
      clearTimeout(this.ddrTimeoutId);
      this.ddrTimeoutId = null;
    }
    if (this.ttlTimeoutId !== null) {
      clearTimeout(this.ttlTimeoutId);
      this.ttlTimeoutId = null;
    }

    // Deliberately NOT resetting the backoff feedback term here: the soft-retry
    // path closes the connection before scheduling the next attempt, and we want
    // the delay to keep escalating across consecutive failures. The term is
    // reset on a successful connect / identity change / revival / reset instead.

    if (this.sseClient) {
      this.log(`Connection closed (${this.clientLabel(this.sseClient)})`);
      this.sseClient.close();
      this.sseClient = null;
      this.callbacks.onDisconnected();
    }

    this.cirReceivedAt = null;
    this.connectionStartedAt = null;
  }

  /**
   * Stop connecting, and stay stopped. Closes the active connection, cancels
   * every pending timer, and makes all subsequent connection attempts no-ops —
   * internal retries included — until {@link resume} is called.
   *
   * For a host that has concluded the path to DUST is dead rather than busy.
   * Ordinary failure handling deliberately retries forever, since the engine
   * cannot tell a dead path from a flapping one; only a host with that context
   * can decide to give up. Because the flag is checked at every connection entry
   * point rather than relying on the close alone, it is safe to call from inside
   * an engine callback: the retry the failing handler goes on to schedule is
   * simply skipped.
   */
  suspend(): void {
    if (this.suspended) {
      return;
    }

    this.log("Suspending: no further connection attempts until resumed");
    this.suspended = true;
    this.close();
  }

  /**
   * Lift a {@link suspend}. Does not itself connect — the caller decides how,
   * via `setConfigAndConnect` (fresh credentials) or `ensureConnection`.
   */
  resume(): void {
    if (!this.suspended) {
      return;
    }

    this.log("Resuming: connection attempts allowed again");
    this.suspended = false;
    this.resetRetryState();
  }

  /**
   * Fully tear down the session: close the connection and drop config, rcs, and
   * per-session counters. Used when no subscribers remain or on destroy.
   */
  reset(): void {
    this.close();
    this.suspended = false;
    this.config = null;
    this.currentRcs = null;
    this.retryCount = 0;
    this.resetBackoff();
    this.consecutiveFetchErrorCount = 0;
    this.reconnectionCount = 0;
    this.gaplessReconnectCount = 0;
    this.hasConnectedAtLeastOnce = false;
    this.heartbeatTimeoutMs = defaultHeartbeatTimeoutMs;
  }
}
